import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import MessageConsole from './MessageConsole';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * CustomerChatButton - Floating chat button for customers
 * Shows unread message count and opens MessageConsole to chat with Calendar Admin
 * 
 * Props:
 * - autoOpen: boolean - if true, automatically open the chat when component mounts
 * - onOpened: function - callback when chat is opened (for clearing autoOpen state)
 */
const CustomerChatButton = ({ autoOpen = false, onOpened }) => {
  const { runningUser, getAuthHeaders } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showConsole, setShowConsole] = useState(false);
  const [calendarAdmin, setCalendarAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(false);
  
  // Refs to prevent multiple fetches
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);
  const autoOpenTriggered = useRef(false);
  
  // Directly check if current running user is a customer
  const isCustomerUser = runningUser?.role === 'customer' || runningUser?.role === 'non_registered_customer';

  // Handle auto-open from Support page
  // Use setTimeout to defer setState and avoid cascading renders warning
  useEffect(() => {
    if (autoOpen && calendarAdmin && !autoOpenTriggered.current) {
      autoOpenTriggered.current = true;
      setTimeout(() => {
        setShowConsole(true);
        if (onOpened) onOpened();
      }, 0);
    }
  }, [autoOpen, calendarAdmin, onOpened]);

  // Fetch the customer's Calendar Admin - only once
  const fetchCalendarAdmin = useCallback(async () => {
    if (!isCustomerUser || !runningUser?.id || fetchingRef.current) {
      setLoading(false);
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/chat-partners`, {
        headers: getAuthHeaders()
      });
      
      if (mountedRef.current && response.data.success && response.data.data.length > 0) {
        const admin = response.data.data.find(p => p.role === 'calendar_admin');
        if (admin) {
          setCalendarAdmin(admin);
        }
      }
    } catch (err) {
      console.error('CustomerChatButton: Error fetching calendar admin:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      fetchingRef.current = false;
    }
  }, [getAuthHeaders, isCustomerUser, runningUser?.id]);

  // Fetch unread count - only for regular messages (not support)
  const fetchUnreadCount = useCallback(async () => {
    if (!isCustomerUser || !runningUser?.id || !calendarAdmin) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/count`, {
        params: { message_type: 'regular' },  // Exclude support messages
        headers: getAuthHeaders()
      });
      
      if (mountedRef.current && response.data.success) {
        const newCount = response.data.unread_count || 0;
        setUnreadCount(prev => {
          if (newCount > prev && prev > 0) {
            setPulse(true);
            setTimeout(() => setPulse(false), 1000);
          }
          return newCount;
        });
      }
    } catch (err) {
      // Silent error - unread count is not critical
    }
  }, [getAuthHeaders, isCustomerUser, runningUser?.id, calendarAdmin]);

  // Fetch data on mount
  useEffect(() => {
    mountedRef.current = true;
    
    if (isCustomerUser && runningUser?.id) {
      fetchCalendarAdmin();
    } else {
      setLoading(false);
      setCalendarAdmin(null);
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [isCustomerUser, runningUser?.id, fetchCalendarAdmin]);

  // Fetch unread count after admin is loaded (only on initial load)
  const initialUnreadFetchedRef = useRef(false);
  
  useEffect(() => {
    if (calendarAdmin && !showConsole && !initialUnreadFetchedRef.current) {
      initialUnreadFetchedRef.current = true;
      fetchUnreadCount();
    }
  }, [calendarAdmin, showConsole, fetchUnreadCount]);

  // Poll for unread count periodically (only when console is closed)
  useEffect(() => {
    if (!isCustomerUser || !runningUser?.id || !calendarAdmin || showConsole) return;
    
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isCustomerUser, runningUser?.id, calendarAdmin, showConsole, fetchUnreadCount]);

  // Callback when messages are loaded successfully
  const handleMessagesLoaded = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Handle close console
  const handleCloseConsole = useCallback(() => {
    setShowConsole(false);
  }, []);

  // Don't render if not a customer
  if (!isCustomerUser) {
    return null;
  }
  
  // Don't render while loading
  if (loading) {
    return null;
  }

  // Don't render if no calendar admin found
  if (!calendarAdmin) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button - Bottom Right */}
      {!showConsole && (
        <button
          onClick={() => setShowConsole(true)}
          className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
            pulse ? 'animate-pulse' : ''
          } ${
            unreadCount > 0 
              ? 'bg-yellow-500 hover:bg-yellow-600' 
              : 'bg-gray-700 hover:bg-gray-800'
          }`}
          title={`Chat with ${calendarAdmin.full_name}`}
          data-testid="customer-chat-button"
        >
          <MessageCircle className="w-6 h-6 text-white" />
          
          {/* Unread Badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Message Console - Regular messages only (not support) */}
      {showConsole && (
        <MessageConsole
          customer={calendarAdmin}
          isOpen={showConsole}
          onClose={handleCloseConsole}
          onMessagesLoaded={handleMessagesLoaded}
          messageType="regular"
        />
      )}
    </>
  );
};

export default CustomerChatButton;
